import { useState, useEffect } from 'react';
import { cloneDeep, merge, isEqual, get } from 'lodash';
import {
  MockHook,
  useSnackbarNotification,
} from '@jupiterone/jupiter-web-toolkit';
import {
  Topic,
  Channel,
  Category,
  UserSettings,
  topicInformation,
  ChannelConfiguration,
} from '@jupiterone/notification-types';
import useNotificationSettings, {
  SlackChannelConfiguration,
} from './hooks/useNotificationSettings';
import { SetUserNotificationSettingsForTopicMutationVariables } from 'navbar/src/jupiterAccountSettings/AccountSettings/Notifications/graphql';
import useSlackIntegrationInstances from 'navbar/src/jupiterAccountSettings/AccountSettings/Notifications/hooks/useSlackIntegrationInstances';
import { generateMockNotificationSettings } from 'navbar/src/jupiterAccountSettings/AccountSettings/Notifications/graphql/generateMockNotificationSettings';

/* Mapping of Topics to topic information grouped by category */
type CategoryTopics = Record<
  Category,
  {
    topic: Topic;
    displayName: string;
    settings: ChannelConfiguration;
  }[]
>;
const getCategoryTopics = (
  notificationSettings?: UserSettings
): CategoryTopics => {
  return Object.entries(topicInformation).reduce(
    (result, [topic, topicInfo]) => {
      const { category, displayName } = topicInfo;
      if (!result[category]) {
        result[category] = [];
      }
      if (!topicInfo.hideFrontendConfiguration) {
        const settings = get(
          notificationSettings,
          `${category}.${topic}`
        ) as ChannelConfiguration;
        result[category].push({
          topic: topic as Topic,
          settings,
          displayName,
        });
      }
      return result;
    },
    {} as CategoryTopics
  );
};

const omitTypename = <T>(key: string, value: keyof T) =>
  key === '__typename' ? undefined : value;
const parseRawApolloData = <T>(payload: T) => {
  return JSON.parse(JSON.stringify(payload), (key, value) =>
    omitTypename<T>(key, value)
  );
};

const useNotificationsPageLogic = () => {
  const snackBarNotification = useSnackbarNotification();
  const {
    notificationSettings,
    slackConfiguration,
    loading: loadingNotificationSettings,
    handleUpsertNotificationSettings,
    refetchNotificationSettings,
  } = useNotificationSettings();
  const {
    slackIntegrationInstances,
    loading: loadingSlackInstances,
  } = useSlackIntegrationInstances();

  const [
    isViewingChannelConfiguration,
    setIsViewingChannelConfiguration,
  ] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [newNotificationSettings, setNewNotificationSettings] = useState<
    UserSettings | undefined
  >(notificationSettings);
  const [
    newSlackConfiguration,
    setNewSlackConfiguration,
  ] = useState<SlackChannelConfiguration>({
    slackChannels: [],
    integrationInstanceId: '',
  });

  useEffect(() => {
    setNewNotificationSettings(notificationSettings);
    slackConfiguration && setNewSlackConfiguration(slackConfiguration);
  }, [notificationSettings, slackConfiguration]);

  const hasSlackConfigurationChanged = !isEqual(
    slackConfiguration,
    newSlackConfiguration
  );
  const hasTopicConfigurationChanged = !isEqual(
    notificationSettings,
    newNotificationSettings
  );

  // Currently only using a Slack configuration even though the
  // schema supports per-topic channel configuration
  // If any configuration has changed, just update each Topic
  const handleSave = async () => {
    if (!newNotificationSettings) {
      return;
    }
    setIsSaving(true);
    const upserts = Object.keys(topicInformation)
      .map((topic) => {
        const { category, hideFrontendConfiguration } = topicInformation[
          topic as Topic
        ];
        // These Topics should not be configurable in the UI
        if (hideFrontendConfiguration) return;
        const newTopicSettings = parseRawApolloData(
          get(newNotificationSettings, `${category}.${topic}`)
        );
        return {
          topic: topic as Topic,
          channelConfigurationUpdate: {
            ...newTopicSettings,
            [Channel.SLACK]: {
              ...newTopicSettings[Channel.SLACK],
              slackChannels: newSlackConfiguration.slackChannels,
              integrationInstanceId:
                newSlackConfiguration.integrationInstanceId,
            },
          },
        };
      })
      .filter(
        Boolean
      ) as SetUserNotificationSettingsForTopicMutationVariables['update'][];
    // Throw error if Slack is enabled without integration/channel set
    if (
      upserts.find(({ channelConfigurationUpdate }) => {
        const {
          enabled,
          slackChannels,
          integrationInstanceId,
        } = channelConfigurationUpdate[Channel.SLACK];
        return (
          enabled && (slackChannels.length === 0 || !integrationInstanceId)
        );
      })
    ) {
      snackBarNotification.error({
        message:
          'Slack integration and channel must be specified in Channel Configuration to enable Slack Notifications',
      });
    } else {
      await Promise.all(
        upserts.map((upsert) => handleUpsertNotificationSettings(upsert))
      );
      await refetchNotificationSettings();
    }
    setIsSaving(false);
  };

  const handleReset = () => {
    setNewNotificationSettings(notificationSettings);
    slackConfiguration && setNewSlackConfiguration(slackConfiguration);
  };

  const handleSlackConfigurationChange = (
    slackChannelConfigurationUpdate: Partial<SlackChannelConfiguration>
  ) =>
    setNewSlackConfiguration((currentConfiguration) => ({
      ...currentConfiguration,
      ...slackChannelConfigurationUpdate,
    }));

  const handleTopicSettingsChange = ({
    category,
    topic,
    channel,
    newEnabledValue,
  }: {
    category: string;
    topic: string;
    channel: string;
    newEnabledValue: boolean;
  }) => {
    const updatedNotificationSettings = merge(
      cloneDeep(newNotificationSettings),
      {
        [category]: {
          [topic]: {
            [channel]: {
              enabled: newEnabledValue,
            },
          },
        },
      }
    );
    setNewNotificationSettings(updatedNotificationSettings);
  };

  const categoryTopics = getCategoryTopics(newNotificationSettings);

  return {
    isSaving,
    loading: loadingNotificationSettings || loadingSlackInstances,
    hasFormChanged:
      hasSlackConfigurationChanged || hasTopicConfigurationChanged,
    slackConfiguration: newSlackConfiguration,
    slackIntegrationInstances,
    isViewingChannelConfiguration,
    categoryTopics,
    handleSave,
    handleReset,
    handleTopicSettingsChange,
    handleSlackConfigurationChange,
    setIsViewingChannelConfiguration,
  };
};

export type NotificationsPageComponentProps = ReturnType<
  typeof useNotificationsPageLogic
>;
export const MOCK_NOTIFICATIONS_PAGE_PROPS: NotificationsPageComponentProps = {
  loading: false,
  isSaving: false,
  hasFormChanged: false,
  slackIntegrationInstances: [{ id: '1', name: '1' }],
  isViewingChannelConfiguration: false,
  slackConfiguration: { slackChannels: ['1'], integrationInstanceId: '1' },
  categoryTopics: getCategoryTopics(generateMockNotificationSettings()),
  handleSave: MockHook.mockFunction,
  handleReset: MockHook.mockFunction,
  handleTopicSettingsChange: MockHook.mockFunction,
  handleSlackConfigurationChange: MockHook.mockFunction,
  setIsViewingChannelConfiguration: MockHook.mockFunction,
};
export default MockHook.useMockHook(
  useNotificationsPageLogic,
  MOCK_NOTIFICATIONS_PAGE_PROPS
);
